import React, { useState } from "react";

import { motion, useReducedMotion } from "framer-motion";
import { MainMenuContextType } from "../../../../menu.types";
import contentItems from "../../constants/contentItems";
import SmallCard from "./SmallCard";
import LargeCard from "./LargeCard";
import TallCard from "./TallCard";
import OfferCard from "../../../shop/components/OfferCard";
import { SPECIAL_OFFER_ITEM, STARTER_PACK } from "../../../shop/constants";
import StarterPackModal from "../../../shop/components/StarterPackModal/StarterPackModal";
import apointsImg from "../../../vip/assets/images/apoints.png";

import {
  gridContainer,
  slideUp,
  smallCardWrapper,
  slideRight,
} from "../../animations/contentGrid.variants";

declare const mp: { trigger: (event: string, ...args: any[]) => void };

// ─── Static derived data ────────────────────
// contentItems is a module-level constant — filter once, not on every render.

const largeItems = contentItems.filter((item) => item.size === "large");
const smallItems = contentItems.filter((item) => item.size === "small");
const tallItems = contentItems.filter((item) => item.size === "tall");

// ─── Types ──────────────────────────────────

interface ContentGridProps {
  changeScreen: MainMenuContextType["changeScreen"];
}

// ─── ContentGrid ────────────────────────────

const ContentGrid: React.FC<ContentGridProps> = ({ changeScreen }) => {
  const shouldReduceMotion = useReducedMotion();
  const [isStarterPackOpen, setIsStarterPackOpen] = useState(false);

  const handleClick = (item: (typeof contentItems)[number]) => {
    if (item.id === STARTER_PACK.id) {
      setIsStarterPackOpen(true);
      return;
    }
    if (item.screen) {
      changeScreen(item.screen);
    } else if (item.redirectTo) {
      try {
        mp.trigger("openUrl", item.redirectTo);
      } catch {
        window.open(item.redirectTo, "_blank");
      }
    }
  };

  return (
    <>
      <motion.div
        className="flex gap-3 flex-1 min-w-0"
        {...(shouldReduceMotion
          ? {}
          : { variants: gridContainer, initial: "hidden", animate: "visible" })}
      >
        {/* Left column — JOB-URI + small cards */}
        <div className="flex flex-col gap-3 flex-1 min-w-0">
          <OfferCard item={SPECIAL_OFFER_ITEM} className="min-h-[170px]" />

          {largeItems.map((item) => (
            <motion.div
              key={item.id}
              {...(shouldReduceMotion ? {} : { variants: slideUp })}
            >
              <LargeCard
                item={item}
                onClick={() => handleClick(item)}
                className="min-h-[230px] 1600px:min-h-[300px] 2560px:min-h-[390px]"
              />
            </motion.div>
          ))}

          <div className="flex gap-3 flex-1">
            {smallItems.map((item) => (
              <motion.div
                key={item.id}
                className="w-full h-full"
                variants={shouldReduceMotion ? undefined : smallCardWrapper}
                whileHover={shouldReduceMotion ? undefined : "hover"}
                whileTap={shouldReduceMotion ? undefined : "tap"}
                style={{ willChange: "transform" }}
              >
                <SmallCard
                  className="h-full w-full 2560px:min-h-[350px]"
                  item={item}
                  onClick={() => handleClick(item)}
                />
              </motion.div>
            ))}
          </div>
        </div>

        {/* Right column — SHOP */}
        {tallItems.map((item) => (
          <motion.div
            key={item.id}
            className="w-[480px] 2560px:w-[560px] flex-shrink-0"
            {...(shouldReduceMotion ? {} : { variants: slideRight })}
          >
            <TallCard
              item={item}
              onClick={() => handleClick(item)}
              fullscreenClick
            />
          </motion.div>
        ))}
      </motion.div>

      <StarterPackModal
        isOpen={isStarterPackOpen}
        onClose={() => setIsStarterPackOpen(false)}
        onClaim={() => setIsStarterPackOpen(false)}
        coverImage={STARTER_PACK.backgroundImage}
        title={STARTER_PACK.name}
        subtitle={STARTER_PACK.description}
        perks={STARTER_PACK.perks}
        badgeLabel={`${STARTER_PACK.apoints} AP`}
        badgeImage={apointsImg}
      />
    </>
  );
};

export default ContentGrid;
